import React, { useEffect, useRef } from "react";
import AIAutomationChat from "../components/AIAutomationChat";

const Communication = () => {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext("2d");
        let width = (canvas.width = window.innerWidth);
        let height = (canvas.height = window.innerHeight);

        const nodes = Array.from({ length: 40 }, () => ({
            x: Math.random() * width,
            y: Math.random() * height,
            r: Math.random() * 2 + 1,
            dx: (Math.random() - 0.5) * 0.5,
            dy: (Math.random() - 0.5) * 0.5,
        }));

        const draw = () => {
            ctx.clearRect(0, 0, width, height);
            ctx.fillStyle = "rgba(100, 180, 255, 0.8)";

            for (let node of nodes) {
                ctx.beginPath();
                ctx.arc(node.x, node.y, node.r, 0, Math.PI * 2);
                ctx.fill();
            }

            for (let i = 0; i < nodes.length; i++) {
                for (let j = i + 1; j < nodes.length; j++) {
                    const dx = nodes[i].x - nodes[j].x;
                    const dy = nodes[i].y - nodes[j].y;
                    const dist = Math.sqrt(dx * dx + dy * dy);
                    if (dist < 120) {
                        ctx.strokeStyle = `rgba(150, 100, 255, ${1 - dist / 120})`;
                        ctx.lineWidth = 0.3;
                        ctx.beginPath();
                        ctx.moveTo(nodes[i].x, nodes[i].y);
                        ctx.lineTo(nodes[j].x, nodes[j].y);
                        ctx.stroke();
                    }
                }
            }

            for (let node of nodes) {
                node.x += node.dx;
                node.y += node.dy;
                if (node.x < 0 || node.x > width) node.dx *= -1;
                if (node.y < 0 || node.y > height) node.dy *= -1;
            }

            requestAnimationFrame(draw);
        };

        draw();

        const handleResize = () => {
            width = canvas.width = window.innerWidth;
            height = canvas.height = window.innerHeight;
        };

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    return (
        <div className="relative bg-gradient-to-br from-gray-950 via-gray-900 to-black text-white min-h-screen overflow-hidden">
            <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />

            {/* Hero Section with Split Layout */}
            <section className="relative min-h-screen w-full flex items-center justify-center px-6 overflow-hidden z-10 pt-20">
                <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

                    {/* Left Column: Text Content */}
                    <div className="text-left animate-fade-in-up order-2 lg:order-1">
                        <h1 className="text-5xl md:text-7xl font-bold mb-6 text-white leading-tight drop-shadow-2xl">
                            Sit back while AI handles<br /> chats, WhatsApp, and calls.
                        </h1>
                        <p className="text-gray-200 text-xl md:text-2xl mb-10 leading-relaxed drop-shadow-lg font-light max-w-lg">
                            One platform for all your customer conversations.
                        </p>

                        <div className="flex flex-col sm:flex-row gap-4 justify-start">
                            <button className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-full font-bold text-lg shadow-lg hover:shadow-blue-500/50 transition-all transform hover:-translate-y-1">
                                Start Free Trial
                            </button>
                            <button className="bg-white/10 border border-white/30 hover:bg-white/20 text-white px-8 py-4 rounded-full font-medium text-lg backdrop-blur-md transition-all">
                                View Demo
                            </button>
                        </div>
                    </div>

                    {/* Right Column: Hero Image */}
                    <div className="relative h-full flex justify-center items-center animate-fade-in order-1 lg:order-2">
                        <div className="relative w-full max-w-[600px] aspect-square">
                            {/* Glowing effect behind the bot */}
                            <div className="absolute inset-0 bg-blue-500/20 blur-[100px] rounded-full animate-pulse-slow"></div>

                            <img
                                src="/images/bot.png"
                                alt="AI Bot handling communication"
                                className="relative w-full h-full object-contain drop-shadow-2xl animate-float"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* Services/Features Section */}
            <section className="py-20 px-8 md:px-16 bg-gradient-to-b from-gray-950 to-gray-900 relative z-10">
                <h2 className="text-4xl font-semibold text-center mb-16">Unified Channels</h2>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
                    {/* Feature 1: Web Chat */}
                    <div className="bg-gray-900 border border-gray-800 hover:border-blue-500 transition-all duration-300 hover:-translate-y-2 rounded-xl p-8 flex flex-col items-center text-center group">
                        <div className="w-20 h-20 bg-blue-900/30 rounded-full flex items-center justify-center mb-6 group-hover:bg-blue-600/20 transition-colors">
                            <svg className="w-10 h-10 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
                        </div>
                        <h3 className="text-2xl font-bold mt-2 mb-3 text-white">Web Chat</h3>
                        <p className="text-gray-400 leading-relaxed">
                            Engage visitors instantly on your website. AI-powered responses ensure 24/7 availability and higher conversion rates.
                        </p>
                    </div>

                    {/* Feature 2: WhatsApp Chat */}
                    <div className="bg-gray-900 border border-gray-800 hover:border-green-500 transition-all duration-300 hover:-translate-y-2 rounded-xl p-8 flex flex-col items-center text-center group">
                        <div className="w-20 h-20 bg-green-900/30 rounded-full flex items-center justify-center mb-6 group-hover:bg-green-600/20 transition-colors">
                            <svg className="w-10 h-10 text-green-400" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.008-.57-.008-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" /></svg>
                        </div>
                        <h3 className="text-2xl font-bold mt-2 mb-3 text-white">WhatsApp Chat</h3>
                        <p className="text-gray-400 leading-relaxed">
                            Reach customers where they spend most of their time. Automated notifications, reminders, and support on WhatsApp.
                        </p>
                    </div>

                    {/* Feature 3: Call Log */}
                    <div className="bg-gray-900 border border-gray-800 hover:border-purple-500 transition-all duration-300 hover:-translate-y-2 rounded-xl p-8 flex flex-col items-center text-center group">
                        <div className="w-20 h-20 bg-purple-900/30 rounded-full flex items-center justify-center mb-6 group-hover:bg-purple-600/20 transition-colors">
                            <svg className="w-10 h-10 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                        </div>
                        <h3 className="text-2xl font-bold mt-2 mb-3 text-white">Call Log</h3>
                        <p className="text-gray-400 leading-relaxed">
                            Never lose track of a conversation. Automatically transcribe, summarize, and log every call for quality assurance.
                        </p>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="text-center py-24 bg-gradient-to-r from-blue-900 via-purple-900 to-pink-900 relative z-10">
                <h2 className="text-4xl font-bold mb-4">Connect with your customers today.</h2>
                <p className="text-gray-200 mb-8 max-w-xl mx-auto">Start aggregating all your channels into one powerful, AI-driven inbox.</p>
                <button className="bg-white hover:bg-gray-100 text-purple-900 px-8 py-4 rounded-full font-bold text-lg shadow-lg transition-colors">
                    Get Started Now
                </button>
            </section>

            {/* Chat Component */}
            <AIAutomationChat />
        </div>
    );
};

export default Communication;
